import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import ScrollAnimation from '../components/ui/ScrollAnimation';
import axios from 'axios';

const Home = () => {
  const [expandedPosts, setExpandedPosts] = useState(new Set());


  // Fetch bulletin posts
  const { data: bulletinPosts = [], isLoading: postsLoading, error: postsError } = useQuery(
    'bulletinPosts',
    async () => {
      const response = await axios.get('/api/posts');
      return response.data;
    }
  );

  const togglePost = (postId) => {
    const newExpandedPosts = new Set(expandedPosts);
    if (newExpandedPosts.has(postId)) {
      newExpandedPosts.delete(postId);
    } else {
      newExpandedPosts.add(postId);
    }
    setExpandedPosts(newExpandedPosts);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative text-white py-48 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1920&h=1080&fit=crop&crop=center')`,
          }}
        >
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <ScrollAnimation animation="fadeInUp" delay={0.2}>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                당신의 미래를 위한
                <br />
                <span className="text-yellow-300">최고의 강의</span>
              </h1>
            </ScrollAnimation>
            
            <ScrollAnimation animation="fadeInUp" delay={0.4}>
              <p className="text-xl md:text-2xl mb-8 text-gray-100 max-w-3xl mx-auto">
                전문가들이 제공하는 고품질 온라인 강의로 새로운 기술을 배우고 경력을 발전시키세요.
              </p>
            </ScrollAnimation>
            
            <ScrollAnimation animation="fadeInUp" delay={0.6}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/courses"
                  className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center"
                >
                  강의 둘러보기
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <Link
                  to="/register-with-subscription"
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors"
                >
                  무료로 시작하기
                </Link>
              </div>
            </ScrollAnimation>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {/* <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-4">
                  <stat.icon className="w-8 h-8 text-primary-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* All Courses CTA Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <ScrollAnimation animation="fadeInUp" delay={0.2}>
              <Link
                to="/courses"
                className="group inline-flex items-center justify-center px-12 py-6 text-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 ease-out relative overflow-hidden"
              >
                {/* Floating animation effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl animate-pulse opacity-75 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Dissolving light shadow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-xl blur-lg opacity-0 group-hover:opacity-50 transition-opacity duration-300 -z-10"></div>
                
                {/* Button content */}
                <span className="relative z-10 flex items-center">
                  모든 강의 보기
                  <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </Link>
            </ScrollAnimation>
          </div>
        </div>
      </section>

      {/* Bulletin Board Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollAnimation animation="fadeInUp" delay={0.2}>
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">게시판</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                중요한 공지사항과 업데이트 정보를 확인하세요.
              </p>
            </div>
          </ScrollAnimation>

          <div className="max-w-4xl mx-auto">
            {postsLoading ? (
              <div className="bg-white rounded-lg shadow-soft p-12 text-center">
                <div className="loading-spinner mx-auto"></div>
                <p className="mt-4 text-gray-600">게시물을 불러오는 중...</p>
              </div>
            ) : postsError ? (
              <div className="bg-white rounded-lg shadow-soft p-12 text-center">
                <p className="text-gray-600">게시물을 불러오는 중 오류가 발생했습니다.</p>
              </div>
            ) : bulletinPosts.length === 0 ? (
              <div className="bg-white rounded-lg shadow-soft p-12 text-center">
                <p className="text-gray-600">아직 게시물이 없습니다.</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-soft overflow-hidden">
                <div className="divide-y divide-gray-200">
                  {bulletinPosts.map((post, index) => (
                    <ScrollAnimation key={post._id} animation="fadeInUp" delay={0.1 * index}>
                      <div>
                        <div
                          onClick={() => togglePost(post._id)}
                          className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <h4 className="font-medium text-gray-900">{post.title}</h4>
                                {post.isPinned && (
                                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                                    고정
                                  </span>
                                )}
                                <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                                  post.category === 'notice' ? 'bg-red-100 text-red-800' :
                                  post.category === 'update' ? 'bg-blue-100 text-blue-800' :
                                  post.category === 'announcement' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {post.category === 'notice' ? '공지사항' :
                                   post.category === 'update' ? '업데이트' :
                                   post.category === 'announcement' ? '안내' : '일반'}
                                </span>
                              </div>
                              <div className="flex items-center text-sm text-gray-500 space-x-4">
                                <span>{post.authorName}</span>
                                <span>•</span>
                                <span>{new Date(post.createdAt).toLocaleDateString('ko-KR')}</span>
                                <span>•</span>
                                <span>조회 {post.views}</span>
                              </div>
                            </div>
                            {expandedPosts.has(post._id) ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                          </div>
                        </div>
                        
                        {/* Expandable Content */}
                        {expandedPosts.has(post._id) && (
                          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                            <div className="prose max-w-none">
                              <div className="whitespace-pre-line text-gray-700 leading-relaxed">
                                {post.content}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </ScrollAnimation>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Bulletin Board Modal */}
      {/* The modal is removed as per the edit hint. */}
    </div>
  );
};

export default Home; 